<template>
  <div id="app">
    <div
      v-show="!isOnline"
      class="offline-mode"
    >
      Offline Mode
      <span v-show="numberOfflineForms > 0">
        ({{ numberOfflineForms }})
      </span>
    </div>

    <div
      v-if="!$store.state.isSelf"
      v-show="inSimulationMode"
      class="simulation-mode"
    >
      Simulating: {{ simulationText }}
    </div>

    <b-container :fluid="true">
      <page-header />
      <form-header />
      <page-alert />
    </b-container>
    <loading ref="loading" />
    <overlay />
    <session-timeout v-if="loggedIn" />
    <b-container
      fluid="xl"
    >
      <transition
        name="page"
        mode="out-in"
      >
        <router-view />
      </transition>
    </b-container>

    <b-modal
      v-if="loggedIn"
      v-model="updateExists"
      id="update-exists"
      modal-class="update-exists"
      centered
      no-close-on-esc
      no-close-on-backdrop
      hide-header
      hide-footer
    >
      <div class="update-exists-icon">
        <img
          src="./assets/warning-icon.png"
          class="icon"
        >
      </div>

      <p class="my-4 text-center">
        An update is available for the BETS-e application.
      </p>

      <b-row>
        <b-col
          cols="12"
          class="pb-3 text-left"
        >
          <b-button
            variant="green"
            class="update-exists-option"
            @click.stop.prevent="refreshApp"
          >
            Update &raquo;
          </b-button>
        </b-col>
      </b-row>

      <template v-slot:modal-header-close>
        &nbsp;
      </template>
    </b-modal>

    <b-modal
      v-if="loggedIn && !isSelfMode"
      v-model="showSendOfflineFormsPrompt"
      id="send-offline-forms"
      modal-class="update-exists"
      centered
      no-close-on-esc
      no-close-on-backdrop
      hide-header
      hide-footer
    >
      <div class="update-exists-icon">
        <img
          src="./assets/warning-icon.png"
          class="icon"
        >
      </div>

      <p class="my-4 text-center">
        {{ numberOfflineFormsText }} If your internet is stable, press below to send them.
      </p>

      <b-row
        v-if="submissionProgress === 0 && !submissionSuccessful && currentRetryAttempt > 0"
      >
        <b-col
          v-if="submissionComplete"
          cols="12"
          :class="submissionCompleteClasses"
        >
          All screenings were not successfully submitted.
          <br>(Attempt {{ currentRetryAttempt }} of {{ retryLimit }})
        </b-col>
      </b-row>

      <b-row>
        <b-col
          cols="12"
          class="pb-3 text-left"
        >
          <b-button
            variant="green"
            class="failed-screening-option"
            @click.stop.prevent="sendOfflineForms()"
            :disabled="submissionProgress > 0"
          >
            Submit {{ screeningText }} &raquo;
          </b-button>
        </b-col>
      </b-row>

      <b-progress
        v-if="submissionProgress > 0"
        height="2rem"
        variant="green"
        :max="numberOfflineForms"
      >
        <b-progress-bar :value="submissionProgress">
          <strong>{{ currentNumberBeingSubmitted }} of {{ numberOfflineForms }}</strong>
        </b-progress-bar>
      </b-progress>

      <template v-slot:modal-header-close>
        &nbsp;
      </template>
    </b-modal>

    <b-modal
      v-if="loggedIn"
      v-model="showResendOptionsModal"
      id="resend-options"
      modal-class="update-exists"
      centered
      hide-header
      hide-footer
    >
      <div class="update-exists-icon">
        <img
          src="./assets/warning-icon.png"
          class="icon"
        >
      </div>

      <p
        v-if="!submissionComplete"
        class="my-4 text-center"
      >
        First, let's try to resend {{ numberOfflineInvalidForms }} {{ invalidScreeningText | toLowerCase }}.
      </p>

      <p
        v-else-if="submissionSuccessful"
        class="my-4 text-center"
      >
        Whew, that went well. Press below to close the support window.
      </p>

      <p
        v-else-if="!diagnosticsSent"
        class="my-4 text-center"
      >
        Dang, looks like there was a problem. Press below to submit a diagnostic report to Branch Support.
      </p>

      <p
        v-else-if="!diagnosticSubmissionSuccessful"
        class="my-4 text-center"
      >
        Well, it is not our day. Please contact branch support. Press below to close the support window.
      </p>

      <p
        v-else
        class="my-4 text-center"
      >
        Got em, let us review the information to identify the issue. We will contact you if more information is needed.
      </p>

      <b-row>
        <b-col
          v-if="submissionComplete"
          cols="12"
          :class="submissionCompleteClasses"
        >
          <span v-if="submissionSuccessful">
            All screenings have been successfully submitted.
          </span>
          <span v-else>
            All screenings were not successfully submitted.
          </span>
        </b-col>
      </b-row>

      <b-row>
        <b-col
          v-if="diagnosticsSent"
          cols="12"
          :class="diagnosticSubmissionCompleteClasses"
        >
          <span v-if="diagnosticSubmissionSuccessful">
            Diagnostic report has been successfully submitted.
          </span>
          <span v-else>
            Diagnostic report has not successfully submitted.
          </span>
        </b-col>
      </b-row>

      <b-row
        v-if="!submissionComplete"
      >
        <b-col
          cols="12"
          class="pb-3"
        >
          <b-button
            variant="green"
            class="failed-screening-option"
            @click.stop.prevent="submitForms(true)"
            :disabled="diagnosticSubmissionInProgress"
          >
            Resend {{ invalidScreeningText }} &raquo;
          </b-button>
        </b-col>
      </b-row>

      <b-row
        v-if="!diagnosticsSent && submissionComplete && !submissionSuccessful"
      >
        <b-col
          cols="12"
          class="pb-3 text-left"
        >
          <b-button
            variant="green"
            class="failed-screening-option"
            @click.stop.prevent="submitDiagnostics"
            :disabled="submissionProgress > 0"
          >
            Submit Diagnostics &raquo;
          </b-button>
        </b-col>
      </b-row>

      <b-row
        v-if="showCloseSupportWindow"
      >
        <b-col
          cols="12"
          class="pb-3 text-left"
        >
          <b-button
            variant="green"
            class="failed-screening-option"
            @click.stop.prevent="hideResendOptionsModal"
          >
            Close Support Window &raquo;
          </b-button>
        </b-col>
      </b-row>

      <b-progress
        v-if="submissionProgress > 0"
        height="2rem"
        variant="green"
        :max="numberOfflineInvalidForms"
      >
        <b-progress-bar :value="submissionProgress">
          <strong>{{ currentNumberBeingSubmitted }} of {{ numberOfflineInvalidForms }}</strong>
        </b-progress-bar>
      </b-progress>

      <template v-slot:modal-header-close>
        &nbsp;
      </template>
    </b-modal>

    <b-modal
      v-if="!updateExists"
      v-model="showInitProgress"
      id="init-progress"
      modal-class="update-exists"
      centered
      no-close-on-esc
      no-close-on-backdrop
      hide-header
      hide-footer
    >
      <p
        v-if="isOnline"
        class="mb-4 text-center"
      >
        Initializing
      </p>

      <p
        v-else
        class="mb-4 text-center"
      >
        Initialization Paused
      </p>

      <b-progress
        height="1rem"
        variant="green"
        :max="initSteps"
      >
        <b-progress-bar :value="initProgress" />
      </b-progress>

      <b-row v-if="!isOnline">
        <b-col
          cols="12"
          class="pt-2 text-center"
        >
          BETS-e has gone offline.<br>Please check your internet connection to continue.
        </b-col>
      </b-row>

      <b-row
        v-if="initFailure && isOnline"
      >
        <b-col
          cols="12"
        >
          <p
            v-if="initFailure"
            class="my-3 text-center"
          >
            BETS-e was unable to retrieve:
          </p>
        </b-col>
      </b-row>

      <b-row
        v-if="initFailure && isOnline"
      >
        <b-col
          cols="8"
          offset="2"
        >
          <ul class="mb-0">
            <li v-if="!visitorStoreLoaded">
              BFM & BFF information
            </li>

            <li v-if="!questionnairesLoaded">
              Screening forms
            </li>

            <li v-if="!hotspotsLoaded">
              Hotspots
            </li>
          </ul>
        </b-col>
      </b-row>

      <b-row
        v-if="initFailure && !showInitSubmitDiagnostics && !diagnosticsSent && isOnline"
      >
        <b-col
          cols="12"
          class="pt-3 text-left"
        >
          <b-button
            variant="green"
            class="failed-screening-option"
            @click.stop.prevent="retryInitialization"
          >
            Retry Initialization {{ retryInitState }} &raquo;
          </b-button>
        </b-col>
      </b-row>

      <b-row
        v-if="showInitSubmitDiagnostics && isOnline"
      >
        <b-col
          cols="12"
          class="pt-3 text-center"
        >
          <span>
            Well that's not good, looks like there was a problem. Press below to submit a diagnostic report to Branch Support.
          </span>

          <b-button
            variant="green"
            class="mt-3 failed-screening-option"
            @click.stop.prevent="submitDiagnostics"
            :disabled="submissionProgress > 0"
          >
            Submit Diagnostics &raquo;
          </b-button>
        </b-col>
      </b-row>

      <b-row>
        <b-col
          v-if="diagnosticsSent"
          cols="12"
          :class="diagnosticInitClasses"
        >
          <span v-if="diagnosticSubmissionSuccessful">
            Got em, let us review the information to identify the issue. We will contact you if more information is needed.
          </span>
          <span v-else>
            Diagnostic report has not successfully submitted. Please contact Branch Support.
          </span>
        </b-col>
      </b-row>

      <b-row v-if="initFailure && isOnline">
        <b-col
          cols="12"
          class="py-3 text-left"
        >
          <b-button
            variant="green"
            class="failed-screening-option"
            @click.stop.prevent="hideShowInitModal"
          >
            Close Window &raquo;
          </b-button>
        </b-col>
      </b-row>

      <template v-slot:modal-header-close>
        &nbsp;
      </template>
    </b-modal>

    <b-modal
      v-model="showAboutModal"
      id="show-about"
      modal-class="update-exists"
      centered
      hide-header
      hide-footer
    >
      <p
        class="mb-2 text-center"
      >
        BETS-e
      </p>

      <b-row>
        <b-col
          cols="6"
          class="py-2 text-right"
        >
          Build Date:
        </b-col>
        <b-col
          cols="6"
          class="py-2"
        >
          {{ buildDate }}
        </b-col>
      </b-row>

      <b-row>
        <b-col
          cols="6"
          class="pb-2 text-right"
        >
          Build Version:
        </b-col>
        <b-col
          cols="6"
          class="pb-2"
        >
          {{ buildVersion }}
        </b-col>
      </b-row>

      <template v-slot:modal-header-close>
        &nbsp;
      </template>
    </b-modal>
  </div>
</template>

<script>
import Loading from './components/Loading'
import { mapActions, mapGetters } from 'vuex'
import { LogEntry } from './models'

export default {
  data () {
    return {
      nodeEnv: '',
      questionnairesLoaded: false,
      hotspotsLoaded: false,
      visitorStoreLoaded: false,
      offlineStoreLoaded: false,
      buildDate: process.env.VUE_APP_COMMIT_DATE,
      buildVersion: process.env.VUE_APP_COMMIT_HASH
    }
  },
  components: {
    loading: Loading,
    overlay: () => import(/* webpackPreload: true */ /* webpackChunkName: "overlay" */ './components/Overlay'),
    'page-alert': () => import(/* webpackPreload: true */ /* webpackChunkName: "page-alert" */ './components/PageAlert'),
    'page-header': () => import(/* webpackPreload: true */ /* webpackChunkName: "page-header" */ './components/PageHeader'),
    'form-header': () => import(/* webpackPreload: true */ /* webpackChunkName: "form-header" */ './components/FormHeader'),
    'session-timeout': () => import(/* webpackPreload: true */ /* webpackChunkName: "session-timeout" */ './components/SessionTimeout')
  },
  created () {
    this.$store._vm.$root.$on('offlineStorageReady', () => {
      this.offlineStoreLoaded = true
      const logEntry = new LogEntry('App/created', 'offlineStorageReady')
      this.addLog(logEntry)
    })
    /**
     * Comment just to build, remove whenever.
     */
  },
  computed: {
    ...mapGetters([
      'initSteps',
      'initProgress',
      'initFailure',
      'initRetryLimit',
      'currentInitRetryAttempt',
      'showInitProgress',
      'showAbout'
    ]),
    ...mapGetters({
      questionnaires: 'forms/questionnaires',
      hotspots: 'forms/hotspots',
      visitorStore: 'branchUsers/allBfmsWithBranchUsers',
      submissionProgress: 'offline/submissionProgress',
      currentNumberBeingSubmitted: 'offline/currentNumberBeingSubmitted',
      showResendOptionsPrompt: 'offline/showResendOptionsPrompt',
      submissionComplete: 'offline/submissionComplete',
      submissionSuccessful: 'offline/submissionSuccessful',
      currentRetryAttempt: 'offline/currentRetryAttempt',
      retryLimit: 'offline/retryLimit',
      diagnosticsSent: 'support/diagnosticsSent',
      diagnosticSubmissionSuccessful: 'support/diagnosticSubmissionSuccessful',
      diagnosticSubmissionInProgress: 'support/diagnosticSubmissionInProgress',
      simulateRetryFailure: 'offline/simulateRetryFailure',
      simulateBranchId: 'authentication/simulateBranchId',
      simulateState: 'authentication/simulateState'
    }),
    assetsLoaded () {
      if (this.loggedIn) {
        return this.questionnairesLoaded && this.visitorStoreLoaded && this.hotspotsLoaded
      }
      return false
    },
    numberOfflineFormsText () {
      return this.numberOfflineForms + (this.numberOfflineForms > 1 ? ' screenings were collected offline are available to submit.' : ' screening was collected offline and is available to submit.')
    },
    numberOfflineInvalidFormsText () {
      return this.numberOfflineInvalidForms + (this.numberOfflineInvalidForms > 1 ? ' screenings could not be sent.' : ' screening could not be sent.')
    },
    screeningText () {
      return (this.numberOfflineForms > 1 ? 'Screenings' : ' Screening')
    },
    invalidScreeningText () {
      return (this.numberOfflineInvalidForms > 1 ? 'Screenings' : ' Screening')
    },
    showResendOptionsModal: {
      get: function () {
        return this.showResendOptionsPrompt
      },
      set: function (show) {
        this.setShowResendOptionsPrompt(show)
      }
    },
    submissionCompleteClasses () {
      const classes = []
      classes.push('pb-3')
      classes.push('text-center')
      classes.push('submission-result')

      if (this.submissionSuccessful) {
        classes.push('success')
      } else {
        classes.push('error')
      }
      return classes
    },
    diagnosticSubmissionCompleteClasses () {
      const classes = []
      classes.push('pb-3')
      classes.push('text-center')
      classes.push('submission-result')

      if (this.diagnosticSubmissionSuccessful) {
        classes.push('success')
      } else {
        classes.push('error')
      }
      return classes
    },
    diagnosticInitClasses () {
      const classes = []
      classes.push('pt-3')
      classes.push('text-center')
      classes.push('submission-result')

      if (this.diagnosticSubmissionSuccessful) {
        classes.push('success')
      } else {
        classes.push('error')
      }
      return classes
    },
    showCloseSupportWindow () {
      if (this.submissionComplete && this.submissionSuccessful) {
        return true
      }

      if (this.diagnosticsSent && this.diagnosticSubmissionSuccessful) {
        return true
      }

      if (this.submissionComplete && !this.submissionSuccessful && this.diagnosticsSent && !this.diagnosticSubmissionSuccessful) {
        return true
      }
      return false
    },
    inSimulationMode () {
      if (this.simulateRetryFailure) {
        return true
      }
      if (this.simulateBranchId) {
        return true
      }
      if (this.simulateState) {
        return true
      }
      return false
    },
    simulationText () {
      const simluationModes = []
      if (this.simulateRetryFailure) {
        simluationModes.push('retry failure')
      }
      if (this.simulateBranchId) {
        simluationModes.push('branch id ' + this.user.primaryBranchId)
      }
      if (this.simulateState) {
        simluationModes.push('state ' + this.user.state)
      }
      return simluationModes.join('; ')
    },
    showInitSubmitDiagnostics () {
      return this.initFailure && !this.diagnosticsSent && (this.initRetryLimit === this.currentInitRetryAttempt)
    },
    retryInitState () {
      let state = ''
      if (this.currentInitRetryAttempt !== 0) {
        state = this.currentInitRetryAttempt + ' of ' + this.initRetryLimit
      }
      return state
    },
    showAboutModal: {
      get () {
        return this.showAbout
      },
      set (value) {
        this.setShowAboutModal(value)
      }
    }
  },
  watch: {
    assetsLoaded: async function (loaded) {
      if (loaded !== undefined && loaded) {
        await this.setApplicationReady(loaded)
        const logEntry = new LogEntry('App/watch/assetsLoaded', 'applicationReady: ' + loaded)
        this.addLog(logEntry)
      }
    },
    questionnaires: {
      handler: function (newValues) {
        if (newValues !== undefined) {
          if (newValues.length > 0) {
            this.questionnairesLoaded = true
          }
          const logEntry = new LogEntry('App/watch/questionnaires', 'questionnairesLoaded: ' + this.questionnairesLoaded)
          this.addLog(logEntry)
        }
      },
      deep: true
    },
    visitorStore: {
      handler: function (newValues) {
        if (newValues !== undefined) {
          if (newValues.length > 0) {
            this.visitorStoreLoaded = true
          }
          const logEntry = new LogEntry('App/watch/visitorStore', 'visitorStoreLoaded: ' + this.visitorStoreLoaded)
          this.addLog(logEntry)
        }
      },
      deep: true
    },
    hotspots: function (newValues) {
      if (newValues !== null) {
        this.hotspotsLoaded = true
        const logEntry = new LogEntry('App/watch/hotspots', 'hotspotsLoaded: ' + this.hotspotsLoaded)
        this.addLog(logEntry)
      }
    }
  },
  methods: {
    ...mapActions([
      'setApplicationReady',
      'setInitStepComplete',
      'setShowInit',
      'performedInitRetryAttempt',
      'setShowAboutModal'
    ]),
    ...mapActions({
      submitForms: 'offline/submitForms',
      setShowResendOptionsPrompt: 'offline/setShowResendOptionsPrompt',
      submitDiagnostics: 'support/submitDiagnostics',
      addLog: 'support/addLog',
      fetchQuestionaires: 'forms/fetchQuestionaires',
      fetchBranchUsers: 'branchUsers/fetchBranchUsers',
      fetchHotspots: 'forms/fetchHotspots'
    }),
    hideResendOptionsModal () {
      this.setShowResendOptionsPrompt(false)
    },
    hideShowInitModal () {
      this.setShowInit(false)
    },
    async retryInitialization () {
      const branchUserObj = {
        branch_id: this.user.primaryBranchId,
        user_id: this.user.id
      }

      if (!this.questionnairesLoaded) {
        await this.fetchQuestionaires(branchUserObj)
      }

      if (!this.hotspotsLoaded) {
        await this.fetchHotspots(this.user.state)
      }

      if (!this.visitorStoreLoaded) {
        await this.fetchBranchUsers(branchUserObj)
      }

      if (!this.assetsLoaded) {
        this.performedInitRetryAttempt()
      }
    },
    async sendOfflineForms () {
      await this.submitForms()
      if (this.numberOfflineForms === 0) {
        this.submitDiagnostics()
      }
    }
  }
}
</script>

<style lang="scss">
body {
  margin: 0;
  padding: 0;
}

#app {
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  height: 100vh;
  widows: 100vw;
  position: relative;
}

*:disabled {
  cursor: not-allowed;
}

.header {
  margin: 0 1rem 1rem 1rem;
  padding-top: 1rem;
  text-align: center;
}

.router-page {
  margin: 0 auto;
  width: 95%;
}

.page-enter-active,
.page-leave-active {
  transition: opacity .2s;
}
.page-enter,
.page-leave-to {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity .15s
}
.fade-enter,
.fade-leave-to {
  opacity: 0
}
.submission-result {
  &.success {
    color: #125E52;
  }

  &.error {
    color: #dc3545;
  }
}

.simulation-mode,
.offline-mode {
  background-color: #dc3545;
  color: #d6d7d8;
  left: 0;
  padding: 5px;
  position: fixed;
}

.offline-mode {
  top: 0;
}

.simulation-mode {
  bottom: 0;
}
</style>
